import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, ChevronDown, ChevronRight, Users, Calendar as CalIcon, Radio, Heart, Search } from "lucide-react";
import api, { formatError } from "@/lib/api";

/**
 * AdminGroupsFolder — Steve (Feb 2026): "let's start a running drop-down
 * folder of all the Group names in the admin section."
 *
 * One row per group. Each row collapses by default (just the headline
 * row). Click to expand and see the analytics for that group inline.
 *
 * Top-level filter so a growing list stays usable when there are
 * hundreds of groups.
 */
export default function AdminGroupsFolder() {
  const [groups, setGroups] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.get("/admin/groups/analytics");
      setGroups(data?.items || []);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter(
      (g) => g.name?.toLowerCase().includes(q) || g.creator_name?.toLowerCase().includes(q)
    );
  }, [groups, query]);

  const toggle = (gid) => setExpandedId((prev) => (prev === gid ? null : gid));

  return (
    <div className="bd-surface p-5" data-testid="admin-groups-folder">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Users size={18} /> Groups folder
          <span
            className="bd-mono text-xs px-2 py-0.5 rounded"
            style={{ background: "var(--bg-soft)", color: "var(--text-dim)" }}
          >
            {groups.length} total
          </span>
        </h2>
        <button
          type="button"
          onClick={load}
          disabled={busy}
          className="bd-btn bd-btn-ghost"
          style={{ padding: "0.35rem 0.75rem", fontSize: "0.8rem" }}
          data-testid="admin-groups-refresh"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : null}
          Refresh
        </button>
      </div>

      <div className="relative mb-3">
        <Search
          size={14}
          style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-dim)" }}
        />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by group name or creator…"
          className="bd-input w-full"
          style={{ paddingLeft: 30 }}
          data-testid="admin-groups-search"
        />
      </div>

      {err && (
        <div
          className="text-sm px-3 py-2 rounded mb-2"
          style={{ background: "color-mix(in srgb, #ff3b30 12%, transparent)", color: "#ff3b30" }}
          data-testid="admin-groups-error"
        >
          {err}
        </div>
      )}

      {busy && groups.length === 0 && (
        <div className="text-sm flex items-center gap-2 py-4" style={{ color: "var(--text-dim)" }}>
          <Loader2 size={14} className="animate-spin" /> Loading groups…
        </div>
      )}

      {!busy && filtered.length === 0 && (
        <div className="text-sm py-4" style={{ color: "var(--text-dim)" }}>
          {query ? "No groups match that filter." : "No groups yet."}
        </div>
      )}

      <ul className="flex flex-col" data-testid="admin-groups-list">
        {filtered.map((g) => {
          const open = expandedId === g.group_id;
          return (
            <li
              key={g.group_id}
              className="border-b"
              style={{ borderColor: "var(--border)" }}
              data-testid={`admin-group-row-${g.group_id}`}
            >
              <button
                type="button"
                onClick={() => toggle(g.group_id)}
                className="w-full flex items-center gap-2 py-2.5 text-left"
                style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--text)" }}
                aria-expanded={open}
                data-testid={`admin-group-toggle-${g.group_id}`}
              >
                {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <span className="font-semibold truncate flex-1 min-w-0">{g.name}</span>
                {g.live_now > 0 && (
                  <span
                    className="bd-chip flex items-center gap-1"
                    style={{
                      background: "#ff3b30",
                      color: "#fff",
                      borderColor: "#ff3b30",
                      padding: "0.1rem 0.45rem",
                      fontSize: "0.62rem",
                    }}
                    title="Live right now"
                  >
                    <Radio size={9} /> LIVE
                  </span>
                )}
                <span
                  className="bd-mono text-xs"
                  style={{ color: "var(--text-dim)" }}
                  title="Members"
                >
                  {g.member_count}m
                </span>
                <span
                  className="bd-mono text-xs"
                  style={{ color: "var(--text-dim)" }}
                  title="Events"
                >
                  {g.event_count}e
                </span>
                <span
                  className="bd-mono text-xs"
                  style={{ color: "var(--text-dim)" }}
                  title="Live sessions in the last 7 days"
                >
                  {g.live_7d}/7d
                </span>
              </button>

              {open && (
                <div className="pb-3 pl-5 pr-1 grid sm:grid-cols-2 gap-2" data-testid={`admin-group-detail-${g.group_id}`}>
                  <Stat icon={Users} label="Members" value={g.member_count} />
                  <Stat icon={CalIcon} label="Events created" value={g.event_count} />
                  <Stat icon={Radio} label="Live sessions (lifetime)" value={g.live_total} />
                  <Stat icon={Radio} label="Live sessions (7d)" value={g.live_7d} />
                  <Stat icon={Heart} label="Group followers" value={g.follower_count} />
                  <Stat icon={CalIcon} label="Active live now" value={g.live_now} />
                  <div className="col-span-full text-xs flex flex-wrap gap-x-4 gap-y-1 pt-1" style={{ color: "var(--text-dim)" }}>
                    <span><strong>Originator:</strong> {g.creator_name}</span>
                    {g.created_at && <span><strong>Created:</strong> {new Date(g.created_at).toLocaleDateString()}</span>}
                    {g.last_active_at && <span><strong>Last activity:</strong> {new Date(g.last_active_at).toLocaleDateString()}</span>}
                  </div>
                  {g.description && (
                    <div className="col-span-full text-xs italic pt-1" style={{ color: "var(--text-dim)" }}>
                      &ldquo;{g.description}&rdquo;
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div
      className="flex items-center gap-2 px-3 py-2 rounded"
      style={{ background: "var(--bg-soft)", border: "1px solid var(--border)" }}
    >
      <Icon size={14} style={{ color: "var(--text-dim)" }} />
      <span className="text-xs" style={{ color: "var(--text-dim)" }}>{label}</span>
      <span className="ml-auto font-bold bd-mono">{value}</span>
    </div>
  );
}
